/**
 * Renders one intelligence module at a time (lazy mount for performance).
 */
import OwnerOperationsIntelligencePanel from '../Owner/OwnerOperationsIntelligencePanel';
import { AdminPumpDataAnalyticsPanel, AdminPumpOwnerDashboardPanel } from './AdminPumpAnalyticsPanel';
import { AdminPumpDiagnosticChecks } from './adminBusinessInsightShared';
import AdminPumpReportExportButton from './AdminPumpReportExportButton';
import AdminPumpSectionShell from './AdminPumpSectionShell';
import { ADMIN_PUMP_EXPORT_IDS } from './adminPumpReportSections';
import { MODULE_META } from './adminPumpHubConfig';

const EXPORT_IDS = {
  'owner-dashboard': ADMIN_PUMP_EXPORT_IDS.ownerDashboard,
  'data-analytics': ADMIN_PUMP_EXPORT_IDS.dataAnalytics,
  'operations-intelligence': ADMIN_PUMP_EXPORT_IDS.intelligence,
  'business-insights': ADMIN_PUMP_EXPORT_IDS.businessInsights,
};

export default function AdminPumpIntelligenceModule({
  moduleId,
  pumpId,
  pumpName = '',
  syncDateRange = null,
  dateRangeKey = 0,
  onReportLoaded = null,
}) {
  const meta = MODULE_META[moduleId] || {};
  const fromDate = syncDateRange?.from_date || '';
  const toDate = syncDateRange?.to_date || '';
  const exportId = EXPORT_IDS[moduleId];

  const exportBtn = exportId ? (
    <AdminPumpReportExportButton
      targetId={exportId}
      filename={`pump-${pumpId}-${moduleId}.pdf`}
      label="Export PDF"
      disabled={!fromDate || !toDate}
      metadata={{
        title: `${pumpName || 'Pump'} — ${meta.title}`,
        subtitle: fromDate && toDate ? `${fromDate} to ${toDate}` : '',
      }}
    />
  ) : null;

  const stepMap = {
    'owner-dashboard': 1,
    'business-insights': 2,
    'data-analytics': 3,
    'operations-intelligence': 4,
  };

  return (
    <AdminPumpSectionShell
      step={stepMap[moduleId]}
      title={meta.title}
      description={meta.description}
      actions={exportBtn}
      feedback={
        !fromDate || !toDate
          ? { tone: 'warning', title: 'Date range required', message: 'Choose a report period above — presets apply immediately.' }
          : null
      }
    >
      {moduleId === 'owner-dashboard' && fromDate && toDate && (
        <AdminPumpOwnerDashboardPanel
          pumpId={pumpId}
          pumpName={pumpName}
          syncDateRange={syncDateRange}
          dateRangeKey={dateRangeKey}
          hideHeader
        />
      )}

      {moduleId === 'business-insights' && fromDate && toDate && (
        <div id={ADMIN_PUMP_EXPORT_IDS.businessInsights}>
          <AdminPumpDiagnosticChecks
            pumpId={Number(pumpId)}
            pumpName={pumpName}
            fromDate={fromDate}
            toDate={toDate}
            dateRangeKey={dateRangeKey}
            compact
          />
        </div>
      )}

      {moduleId === 'data-analytics' && fromDate && toDate && (
        <AdminPumpDataAnalyticsPanel
          pumpId={pumpId}
          pumpName={pumpName}
          syncDateRange={syncDateRange}
          dateRangeKey={dateRangeKey}
          hideHeader
          onReportLoaded={onReportLoaded}
        />
      )}

      {moduleId === 'operations-intelligence' && fromDate && toDate && (
        <div id={ADMIN_PUMP_EXPORT_IDS.intelligence}>
          <OwnerOperationsIntelligencePanel
            pumpId={Number(pumpId)}
            syncDateRange={syncDateRange}
          />
        </div>
      )}
    </AdminPumpSectionShell>
  );
}
